import { ModelControlEnum, type MoveConflictModalData } from '@qse/constants'
import { useActiveModelStore, useUserStore } from '@qse/stores'
import { addItemToSortedCache, shouldSkipCacheUpdatesWithLog } from '@qse/utils'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  abortFileUpload,
  bulkAbortUploads,
  bulkCompleteUploads,
  bulkCopyFiles,
  bulkMoveFiles,
  bulkMoveToTrash,
  bulkPermanentDelete,
  bulkRestore,
  bulkToggleFavorite,
  cancelFolderUpload,
  completeFolderUpload,
  completeMultipartUpload,
  completeSingleUpload,
  createFolder,
  emptyTrash,
  findDownload,
  getFile,
  getFolderDetails,
  getMultipartFileSignedUrls,
  initiateFolderUpload,
  lookupFile,
  requestDownload,
  retryFileEncryption,
  searchFiles,
  startBatchFileUpload,
  startMultipartUpload,
  startSingleUpload,
  updateFile,
} from '../file-management'
import { queryKeys, queryOptions } from '../react-query-config'
import type {
  AbortUploadResponseDto,
  BatchFileUploadRequestDto,
  BatchFileUploadResponseDto,
  BulkMoveFileDto,
  BulkOperationDto,
  BulkOperationResponseDto,
  BulkUploadCompleteRequestDto,
  CompleteMultipartUploadDto,
  CompleteMultipartUploadResponseDto,
  CompleteSingleUploadDto,
  CompleteSingleUploadResponseDto,
  CopyFilesDto,
  CreateFolderDto,
  CreateFolderResponseDto,
  DownloadInfoDto,
  DownloadRequestResponseDto,
  FileListingItemDto,
  FilesSearchDto,
  FilesSearchResponse,
  FolderUploadCancelResponseDto,
  FolderUploadCompleteResponseDto,
  FolderUploadInitiateDto,
  FolderUploadInitiateResponseDto,
  LookupFileResponseDto,
  MediaType,
  MultipartFileSignedUrlsResponseDto,
  ResumeMultipartUploadUrlDto,
  RetryFileEncryptionResponseDto,
  StartMultipartUploadResponseDto,
  StartMultipartUploadUrlDto,
  StartSingleUploadResponseDto,
  StartSingleUploadUrlDto,
  UpdateFileDto,
  UpdateFileResponseDto,
} from '../types'

// File upload hooks
export const useStartSingleUpload = () => {
  return useMutation<StartSingleUploadResponseDto, Error, StartSingleUploadUrlDto>({
    mutationFn: startSingleUpload,
    onSuccess: () => {
      // No cache update needed - file not created yet, only upload URL generated
      // Cache will be updated when upload completes via useCompleteSingleUpload
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useCompleteSingleUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<CompleteSingleUploadResponseDto, Error, CompleteSingleUploadDto>({
    mutationFn: completeSingleUpload,
    onSuccess: data => {
      // Check if we're in trash or favorites context - skip cache updates if so
      if (shouldSkipCacheUpdatesWithLog('both', 'single file upload completion')) {
        return
      }

      // Update cache directly with the new file
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData || !data.file) return

        const fileToAdd = { ...data.file }

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = [...cacheData.pages]

          if (updatedPages[0]) {
            const firstPage = updatedPages[0]
            const fileExists = firstPage.data?.some(
              (f: any) => f.id === data.file.id || f.name === data.file.name,
            )

            if (!fileExists && firstPage.data) {
              updatedPages[0] = {
                ...firstPage,
                data: [fileToAdd, ...firstPage.data],
                totalCount: (firstPage.totalCount || 0) + 1,
              }
              queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
            }
          }
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const fileExists = cacheData.data.some(
            (f: any) => f.id === data.file.id || f.name === data.file.name,
          )

          if (!fileExists) {
            queryClient.setQueryData(queryKey, {
              ...cacheData,
              data: [fileToAdd, ...cacheData.data],
              totalCount: cacheData.totalCount + 1,
            })
          }
        }
      })

      // Also update file detail query to ensure components see correct status
      if (data.file) {
        queryClient.setQueryData(queryKeys.files.detail(data.file.id), data.file)
      }
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useStartMultipartUpload = () => {
  return useMutation<StartMultipartUploadResponseDto, Error, StartMultipartUploadUrlDto>({
    mutationFn: startMultipartUpload,
    onSuccess: () => {
      // No cache update needed - file not created yet, only upload initiated
      // Cache will be updated when upload completes via useCompleteMultipartUpload
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useGetMultipartFileSignedUrls = () => {
  return useMutation<MultipartFileSignedUrlsResponseDto, Error, ResumeMultipartUploadUrlDto>({
    mutationFn: getMultipartFileSignedUrls,
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useCompleteMultipartUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<CompleteMultipartUploadResponseDto, Error, CompleteMultipartUploadDto>({
    mutationFn: completeMultipartUpload,
    onSuccess: data => {
      // Check if we're in trash or favorites context - skip cache updates if so
      if (shouldSkipCacheUpdatesWithLog('both', 'multipart upload completion')) {
        return
      }

      // Update cache directly with the new file
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData || !data.file) return

        const fileToAdd = { ...data.file }

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = [...cacheData.pages]

          if (updatedPages[0]) {
            const firstPage = updatedPages[0]
            const fileExists = firstPage.data?.some(
              (f: any) => f.id === data.file.id || f.name === data.file.name,
            )

            if (!fileExists && firstPage.data) {
              updatedPages[0] = {
                ...firstPage,
                data: [fileToAdd, ...firstPage.data],
                totalCount: (firstPage.totalCount || 0) + 1,
              }
              queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
            }
          }
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const fileExists = cacheData.data.some(
            (f: any) => f.id === data.file.id || f.name === data.file.name,
          )

          if (!fileExists) {
            queryClient.setQueryData(queryKey, {
              ...cacheData,
              data: [fileToAdd, ...cacheData.data],
              totalCount: cacheData.totalCount + 1,
            })
          }
        }
      })

      // Also update file detail query to ensure components see correct status
      if (data.file) {
        queryClient.setQueryData(queryKeys.files.detail(data.file.id), data.file)
      }
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

// Folder operations
export const useCreateFolder = () => {
  const queryClient = useQueryClient()

  return useMutation<CreateFolderResponseDto, Error, CreateFolderDto>({
    mutationFn: createFolder,
    onSuccess: data => {
      // Check if we're in trash or favorites context - skip cache updates if so
      if (shouldSkipCacheUpdatesWithLog('both', 'folder creation')) {
        return
      }

      // Update cache directly with the new folder
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData || !data.folder) return

        const folderToAdd = { ...data.folder }

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = [...cacheData.pages]

          if (updatedPages[0]) {
            const firstPage = updatedPages[0]
            const folderExists = firstPage.data?.some(
              (f: any) => f.id === data.folder.id || f.name === data.folder.name,
            )

            if (!folderExists && firstPage.data) {
              const updatedFirstPage = addItemToSortedCache(firstPage, folderToAdd)

              updatedPages[0] = updatedFirstPage
              queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
            }
          }
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const folderExists = cacheData.data.some(
            (f: any) => f.id === data.folder.id || f.name === data.folder.name,
          )

          if (!folderExists) {
            queryClient.setQueryData(queryKey, addItemToSortedCache(cacheData, folderToAdd))
          }
        }
      })

      // Also update file detail query
      if (data.folder) {
        queryClient.setQueryData(queryKeys.files.detail(data.folder.id), data.folder)
      }
    },
  })
}

// File operations
export const useGetFile = (params: {
  id?: string
  path?: string
  enabled?: boolean
}): UseQueryResult<FileListingItemDto, Error> => {
  const { user } = useUserStore()

  const shouldFetchByDefault = !!(params.id || params.path) && user?.userRole !== 'SUPER_ADMIN'

  return useQuery<FileListingItemDto>({
    queryKey: queryKeys.files.detail(params.id || params.path || ''),
    queryFn: () => getFile(params),
    enabled: params.enabled !== undefined ? params.enabled : shouldFetchByDefault,
    ...queryOptions.files,
  })
}

export const useLookupFile = (params: {
  name: string
  parentId?: string
  parentPath?: string
}): UseQueryResult<LookupFileResponseDto, Error> => {
  const { user } = useUserStore()

  return useQuery<LookupFileResponseDto>({
    queryKey: queryKeys.files.lookup(params.name, params.parentId || params.parentPath || ''),
    queryFn: () => lookupFile(params),
    enabled: !!params.name && user?.userRole !== 'SUPER_ADMIN',
    ...queryOptions.files,
  })
}

export const useGetFolderDetails = (params: {
  id?: string
  path?: string
  limit?: number
  offset?: number
  search?: string
  mediaType?: MediaType
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
  sortOrder?: 'ASC' | 'DESC'
  createdFrom?: string
  createdTo?: string
  location?: string
}): UseQueryResult<FilesSearchResponse, Error> => {
  const { user } = useUserStore()

  return useQuery<FilesSearchResponse>({
    queryKey: queryKeys.files.folderDetails(params.id || params.path || ''),
    queryFn: () => getFolderDetails(params),
    enabled: !!(params.id || params.path) && user?.userRole !== 'SUPER_ADMIN',
    ...queryOptions.files,
  })
}

export const useUpdateFile = () => {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateFileResponseDto,
    Error & { status?: number; data?: any },
    { data: UpdateFileDto; params: { id?: string; path?: string } }
  >({
    mutationFn: async ({ data, params }) => {
      try {
        return await updateFile(data, params)
      } catch (err: any) {
        // ---- normalize here, preserve status ----
        const status =
          err?.response?.status ??
          err?.status ??
          err?.statusCode ??
          err?.response?.data?.status ??
          err?.response?.data?.statusCode

        const message =
          err?.response?.data?.error?.message ??
          err?.response?.data?.message ??
          err?.message ??
          'Failed to update file'

        const wrapped = Object.assign(new Error(message), {
          status,
          data: err?.response?.data,
          original: err,
        })

        throw wrapped
      }
    },

    onSuccess: (data, { params }) => {
      // Update the specific file detail query
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.detail(params.id || params.path || ''),
      })

      // Update cache directly with the updated file
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData || !data.file) return

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page

            return {
              ...page,
              data: page.data.map((f: any) =>
                f.id === data.file.id || f.id === params.id || f.path === params.path
                  ? { ...f, ...data.file }
                  : f,
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: cacheData.data.map((f: any) =>
              f.id === data.file.id || f.id === params.id || f.path === params.path
                ? { ...f, ...data.file }
                : f,
            ),
          })
        }
      })
    },

    mutationKey: ['updateFile'],
  })
}

export const useSearchFiles = (
  searchParams: FilesSearchDto,
): UseQueryResult<FilesSearchResponse, Error> => {
  const { user } = useUserStore()

  return useQuery<FilesSearchResponse>({
    queryKey: queryKeys.files.list(searchParams),
    queryFn: () => searchFiles(searchParams),
    enabled: user?.userRole !== 'SUPER_ADMIN',
    ...queryOptions.files,
  })
}

// Infinite query version of useSearchFiles
export const useSearchFilesInfinite = (
  searchParams: Omit<FilesSearchDto, 'offset'>,
  options?: {
    enabled?: boolean
  },
) => {
  const { user } = useUserStore()

  const isUserLoaded = !!user?.userRole

  return useInfiniteQuery({
    queryKey: queryKeys.files.list(searchParams),
    queryFn: async ({ pageParam = 0 }) => {
      const params = {
        ...searchParams,
        offset: pageParam,
      }

      return searchFiles(params)
    },
    getNextPageParam: (lastPage, allPages) => {
      // Count actual items in cache (handles deletions correctly)
      const loadedItemsCount = allPages.reduce((total, page) => {
        return total + (page.data?.length || 0)
      }, 0)

      // Use loaded items count as next offset
      if (loadedItemsCount < lastPage.totalCount) {
        return loadedItemsCount
      }

      return undefined
    },
    initialPageParam: 0,
    enabled: isUserLoaded && (options?.enabled ?? true) && user.userRole !== 'SUPER_ADMIN',
    ...queryOptions.files,
  })
}

// Advanced folder search hook with comprehensive parameters
export const useSearchFolders = (searchParams: {
  limit?: number
  offset?: number
  parentPath?: string
  parentId?: string
  search?: string
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'sizeBytes'
  sortOrder?: 'ASC' | 'DESC'
  createdFrom?: string
  isDir?: boolean
  createdTo?: string
  mediaType?: MediaType
  location?: string
}): UseQueryResult<FilesSearchResponse, Error> => {
  const { user } = useUserStore()
  const enhancedSearchParams: FilesSearchDto = {
    ...searchParams,
  }

  return useQuery<FilesSearchResponse>({
    queryKey: queryKeys.files.list(enhancedSearchParams),
    queryFn: () => searchFiles(enhancedSearchParams),
    enabled: user?.userRole !== 'SUPER_ADMIN',
    ...queryOptions.files,
  })
}

// Download operations
export const useRequestDownload = () => {
  return useMutation<DownloadRequestResponseDto, Error, { id?: string; path?: string }>({
    mutationFn: requestDownload,
  })
}

export const useFindDownload = (params: {
  id?: string
  path?: string
}): UseQueryResult<DownloadInfoDto, Error> => {
  return useQuery<DownloadInfoDto>({
    queryKey: queryKeys.files.download(params.id || params.path || ''),
    queryFn: () => findDownload(params),
    enabled: !!(params.id || params.path),
    refetchInterval: query => (query.state.data?.status === 'PROCESSING' ? 2000 : false), // Poll every 2 seconds if processing
    ...queryOptions.files,
  })
}

// Bulk operations
export const useBulkMoveFilesWithConflictResolution = (
  onConflictDetected?: (
    response: BulkOperationResponseDto,
    request: BulkMoveFileDto,
  ) => Promise<void>,
) => {
  const queryClient = useQueryClient()
  const { toggle } = useActiveModelStore()

  return useMutation<BulkOperationResponseDto, Error, BulkMoveFileDto>({
    mutationFn: bulkMoveFiles,
    onSuccess: async (response, variables) => {
      // Handle conflicts - trigger conflict resolution modal
      if (response.conflictCount && response.conflictCount > 0) {
        if (onConflictDetected) {
          await onConflictDetected(response, variables)
          toggle()
        }

        return
      }

      // Handle the response based on success/failure counts
      if (response.failureCount === 0) {
        // All items moved successfully
        toast.success(`Successfully moved ${response.successCount} item(s)`)
      } else if (response.successCount === 0) {
        // All items failed
        const errorMessage =
          response.failures.length > 0 ? response.failures[0].error : 'Failed to move items'

        toast.error(`Move failed: ${errorMessage}`)
      } else {
        // Partial success - some items succeeded, some failed
        const successMessage = `${response.successCount} of ${variables.fileIds.length} items moved successfully`

        toast.success(successMessage)

        // Show detailed error for failed items
        if (response.failures.length > 0) {
          const failedNames = response.failures.slice(0, 3).map(failure => failure.fileId)
          const remainingFailed = response.failures.length - 3

          const failureMessage = `Failed to move: ${failedNames.join(', ')}${remainingFailed > 0 ? ` and ${remainingFailed} more` : ''}`

          // Show specific error details for the first few failures
          const errorDetails = response.failures
            .slice(0, 3)
            .map(failure => `${failure.fileId}: ${failure.error}`)
            .join('; ')

          toast.error(`${failureMessage}. ${errorDetails}`)
        }
      }

      // Comprehensive UI updates for both source and destination
      // 1. Update cache to remove moved files from source locations
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const successfulIdsSet = new Set(response.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })

      // 2. If there's a target destination, invalidate its folder details
      // (We need to invalidate because we don't have the full file data to add)
      if (variables.targetParentId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files.folderDetails(variables.targetParentId),
        })
      }

      // 3. Invalidate any file detail queries for moved files
      response.successfulIds.forEach(fileId => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.files.detail(fileId),
        })
      })

      // 4. Invalidate any parent folder details that might be affected
      // This ensures breadcrumb navigation and folder counts are updated
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.details(),
      })
    },
    onError: error => {
      toast.error(`Move operation failed: ${error.message}`)
    },
  })
}

// Hook that integrates conflict resolution with the move operation
export const useBulkMoveFiles = () => {
  const { setModelActive, toggle, setData, setWidth, toggleModel } = useActiveModelStore()

  const baseMutation = useBulkMoveFilesWithConflictResolution()

  const handleConflictDetected = async (
    response: BulkOperationResponseDto,
    request: BulkMoveFileDto,
  ): Promise<void> => {
    if (response.conflictedFiles && response.conflictedFiles.length > 0) {
      return new Promise<void>(resolve => {
        const modalData: MoveConflictModalData = {
          conflictedFiles: response.conflictedFiles || [],
          originalRequest: request,
          onResolutionComplete: async finalRequest => {
            try {
              if (!finalRequest) {
                // User cancelled or all files were skipped
                resolve()

                return
              }

              // Execute the final move request with conflict resolution
              await baseMutation.mutateAsync(finalRequest)
              // Success will be handled by the base mutation's onSuccess
              resolve()
            } catch {
              // Error will be handled by the base mutation's onError
              // Silently handle error as it's already handled by the mutation
              resolve()
            }
          },
        }

        // Set modal data and open conflict resolution modal
        setData(modalData)
        setModelActive(ModelControlEnum.MOVE_CONFLICT)
        setWidth('max-w-[600px]')
        if (!toggleModel) {
          toggle()
        }
      })
    }
  }

  return useBulkMoveFilesWithConflictResolution(handleConflictDetected)
}

export const useBulkCopyFiles = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, CopyFilesDto>({
    mutationFn: bulkCopyFiles,
    onSuccess: () => {
      // For copy operations, we need to invalidate because we don't get the full copied file data
      // The files are copied to a new location, so we need fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.files.lists() })
    },
  })
}

export const useBulkMoveToTrash = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, BulkOperationDto>({
    mutationFn: bulkMoveToTrash,
    onSuccess: async data => {
      // Note: Removed folder upload cancellation logic when moving folders to trash
      // This allows uploads to continue even when the destination folder is moved to trash
      // Only "delete permanent" actions will cancel uploads (handled in useBulkPermanentDelete)

      // Show success toast for successful operations
      if (data.successCount > 0) {
        const successMessage =
          data.successCount === 1
            ? '1 item moved to trash'
            : `${data.successCount} items moved to trash`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const failureMessage =
          data.failureCount === 1
            ? '1 item failed to move to trash'
            : `${data.failureCount} items failed to move to trash`

        toast.error(failureMessage)
      }

      // Update cache to remove trashed files
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      console.log('🗑️ [useBulkMoveToTrash] Removing files from cache:', {
        successfulIds: data.successfulIds,
        totalQueries: queriesToUpdate.length,
      })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        // Convert successful IDs to strings for comparison
        const successfulIdsSet = new Set(data.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          console.log('🗑️ [useBulkMoveToTrash] Updating infinite query')

          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page

            const beforeCount = page.data.length
            const updatedData = page.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))
            const removed = beforeCount - updatedData.length

            if (removed > 0) {
              console.log('🗑️ [useBulkMoveToTrash] Removed from page:', {
                beforeCount,
                afterCount: updatedData.length,
                removed,
              })
            }

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(0, (page.totalCount || 0) - removed),
            }
          })

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            pages: updatedPages,
          })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const beforeCount = cacheData.data.length
          const updatedData = cacheData.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))
          const removed = beforeCount - updatedData.length

          console.log('🗑️ [useBulkMoveToTrash] Updated regular query:', {
            beforeCount,
            afterCount: updatedData.length,
            removed,
          })

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(0, cacheData.totalCount - removed),
          })
        }
      })
    },
  })
}

export const useBulkRestore = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, BulkOperationDto>({
    mutationFn: bulkRestore,
    onSuccess: data => {
      // Show success toast for successful operations
      if (data.successCount > 0) {
        const successMessage =
          data.successCount === 1 ? '1 item restored' : `${data.successCount} items restored`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const failureMessage =
          data.failureCount === 1
            ? '1 item failed to restore'
            : `${data.failureCount} items failed to restore`

        toast.error(failureMessage)
      }

      // Update cache to remove restored files (they're no longer in trash)
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const successfulIdsSet = new Set(data.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })
    },
  })
}

export const useBulkPermanentDelete = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, BulkOperationDto>({
    mutationFn: bulkPermanentDelete,
    onSuccess: data => {
      // Show success toast for successful operations
      if (data.successCount > 0) {
        const successMessage =
          data.successCount === 1
            ? '1 item permanently deleted'
            : `${data.successCount} items permanently deleted`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const failureMessage =
          data.failureCount === 1
            ? '1 item failed to delete permanently'
            : `${data.failureCount} items failed to delete permanently`

        toast.error(failureMessage)
      }

      // 🗑️ Track permanently deleted folders in upload tracker
      // This enables graceful failure for files being uploaded to deleted destinations
      if (data.successfulIds && data.successfulIds.length > 0) {
        try {
          // Dynamically import to avoid circular dependencies
          import('@qse/ui').then(({ useGlobalFileUpload }) => {
            const uploadStore = useGlobalFileUpload.getState()

            // Use data.successfulIds from the API response
            // This gives us the actual list of folders that were successfully deleted
            uploadStore.checkAndAbortAffectedFiles(data.successfulIds)
          })
        } catch (error) {
          console.error('Failed to track deleted folders in upload system:', error)
        }
      }

      // Update cache to remove permanently deleted files
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const successfulIdsSet = new Set(data.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })
    },
  })
}

export const useBulkToggleFavorite = (
  files?: Array<{ id: string | number; isFavorite?: boolean }>,
) => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, BulkOperationDto>({
    mutationFn: bulkToggleFavorite,
    onSuccess: (data, variables) => {
      // Determine if we're adding to or removing from favorites
      let isAddingToFavorites = false

      if (files && variables.fileIds.length > 0) {
        // Check the first file's current favorite status to determine the action
        const firstFile = files.find(f => f.id.toString() === variables.fileIds[0])

        if (firstFile) {
          // If currently not favorited, we're adding to favorites
          // If currently favorited, we're removing from favorites
          isAddingToFavorites = !firstFile.isFavorite
        }
      }

      // Show success toast for successful operations
      if (data.successCount > 0) {
        const action = isAddingToFavorites ? 'added to' : 'removed from'
        const successMessage =
          data.successCount === 1
            ? `1 item ${action} favorites`
            : `${data.successCount} items ${action} favorites`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const action = isAddingToFavorites ? 'add to' : 'remove from'
        const failureMessage =
          data.failureCount === 1
            ? `1 item failed to ${action} favorites`
            : `${data.failureCount} items failed to ${action} favorites`

        toast.error(failureMessage)
      }

      // Update cache to toggle favorite status for files
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const successfulIdsSet = new Set(data.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page

            return {
              ...page,
              data: page.data.map((f: any) =>
                successfulIdsSet.has(String(f.id)) ? { ...f, isFavorite: isAddingToFavorites } : f,
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: cacheData.data.map((f: any) =>
              successfulIdsSet.has(String(f.id)) ? { ...f, isFavorite: isAddingToFavorites } : f,
            ),
          })
        }
      })
    },
    onError: e => {
      toast.error(e.message)
    },
  })
}

export const useRetryFileEncryption = () => {
  const queryClient = useQueryClient()

  return useMutation<RetryFileEncryptionResponseDto, Error, string>({
    mutationFn: retryFileEncryption,
    onSuccess: (_data, fileId) => {
      toast.success('Item encryption retry initiated successfully')

      // Update cache to reflect the file status change
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page

            return {
              ...page,
              data: page.data.map((f: any) =>
                f.id === fileId ? { ...f, status: 'ENCRYPTING' } : f,
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: cacheData.data.map((f: any) =>
              f.id === fileId ? { ...f, status: 'ENCRYPTING' } : f,
            ),
          })
        }
      })
    },
    onError: error => {
      toast.error(`Failed to retry item encryption: ${error.message}`)
    },
  })
}

export const useAbortFileUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<AbortUploadResponseDto, Error, string>({
    mutationFn: abortFileUpload,
    onSuccess: (_data, fileId) => {
      toast.success('Item upload aborted successfully')

      // Update cache to remove aborted file
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => String(f.id) !== fileId)

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => String(f.id) !== fileId)

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })

      // Force active queries to refetch after cache update
      // This ensures UI updates immediately and components see the changes
      queryClient.invalidateQueries({ queryKey: filesListKey })
    },
    onError: error => {
      toast.error(`Failed to abort item upload: ${error.message}`)
    },
  })
}

// Folder Upload Hooks
export const useInitiateFolderUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<FolderUploadInitiateResponseDto, Error, FolderUploadInitiateDto>({
    mutationFn: initiateFolderUpload,
    onSuccess: data => {
      // Check if we're in trash or favorites context - skip cache updates if so
      if (shouldSkipCacheUpdatesWithLog('both', 'folder upload initiation')) {
        return
      }

      toast.success('Folder upload initiated successfully')

      console.log('📂 [useInitiateFolderUpload] Folder initiated:', {
        id: data.id,
        name: data.name,
        fullData: data,
      })

      // Update cache with the newly created folder
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      console.log('📂 [useInitiateFolderUpload] Found queries:', {
        count: queriesToUpdate.length,
        queryKeys: queriesToUpdate.map(([key]) => key),
      })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) {
          console.log('📂 [useInitiateFolderUpload] No cache data for query:', queryKey)

          return
        }

        const folderToAdd = { ...data }

        console.log('📂 [useInitiateFolderUpload] Cache structure:', {
          queryKey,
          hasPages: !!cacheData.pages,
          hasData: !!cacheData.data,
          pagesCount: cacheData.pages?.length,
          dataCount: cacheData.data?.length,
        })

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          console.log('📂 [useInitiateFolderUpload] Updating infinite query')
          const updatedPages = [...cacheData.pages]

          if (updatedPages[0]) {
            const firstPage = updatedPages[0]

            console.log('📂 [useInitiateFolderUpload] First page:', {
              hasData: !!firstPage.data,
              dataLength: firstPage.data?.length,
              totalCount: firstPage.totalCount,
            })

            const folderExists = firstPage.data?.some(
              (f: any) => f.id === data.id || f.name === data.name,
            )

            console.log('📂 [useInitiateFolderUpload] Folder exists?', folderExists)

            if (!folderExists && firstPage.data) {
              const updatedFirstPage = addItemToSortedCache(firstPage, folderToAdd)

              updatedPages[0] = updatedFirstPage
              queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
              console.log('📂 [useInitiateFolderUpload] ✅ Added folder to first page:', {
                newDataLength: updatedPages[0].data.length,
                newTotalCount: updatedPages[0].totalCount,
              })
            }
          }
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          console.log('📂 [useInitiateFolderUpload] Updating regular query')
          const folderExists = cacheData.data.some(
            (f: any) => f.id === data.id || f.name === data.name,
          )

          if (!folderExists) {
            queryClient.setQueryData(queryKey, addItemToSortedCache(cacheData, folderToAdd))
            console.log('📂 [useInitiateFolderUpload] ✅ Added folder to regular query')
          }
        }
      })
    },
    onError: error => {
      toast.error(`Failed to initiate folder upload: ${error.message}`)
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useStartBatchFileUpload = () => {
  return useMutation<
    BatchFileUploadResponseDto,
    Error,
    { folderId: string; data: BatchFileUploadRequestDto }
  >({
    mutationFn: ({ folderId, data }) => startBatchFileUpload(folderId, data),
    onSuccess: () => {
      // No cache update needed - files not created yet, only batch initiated
      // Cache will be updated when batch completes via useBulkCompleteUploads
    },
    onError: error => {
      toast.error(`Failed to start batch file upload: ${error.message}`)
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useCancelFolderUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<FolderUploadCancelResponseDto, Error, string>({
    mutationFn: cancelFolderUpload,
    onSuccess: (_data, folderId) => {
      // Update cache to remove cancelled folder and its contents
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => String(f.id) !== folderId)

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => String(f.id) !== folderId)

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })
    },
    onError: error => {
      toast.error(`Failed to cancel folder upload: ${error.message}`)
    },
  })
}

export const useBulkCompleteUploads = () => {
  const queryClient = useQueryClient()

  return useMutation<
    BulkOperationResponseDto,
    Error,
    { folderId: string; data: BulkUploadCompleteRequestDto }
  >({
    mutationFn: ({ folderId, data }) => bulkCompleteUploads(folderId, data),
    onSuccess: data => {
      // Show success toast for successful operations
      if (data.successCount > 0) {
        const successMessage =
          data.successCount === 1
            ? '1 upload completed successfully'
            : `${data.successCount} uploads completed successfully`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const failureMessage =
          data.failureCount === 1
            ? '1 upload failed to complete'
            : `${data.failureCount} uploads failed to complete`

        toast.error(failureMessage)
      }

      // Invalidate queries to get fresh data with completed uploads
      // We can't use setQueryData here because we don't have the full file data in the response
      queryClient.invalidateQueries({ queryKey: queryKeys.files.lists() })
    },
    onError: error => {
      toast.error(`Failed to complete uploads: ${error.message}`)
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useEmptyTrash = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, void>({
    mutationFn: emptyTrash,
    onSuccess: data => {
      // Show success toast
      if (data.successCount > 0) {
        const successMessage =
          data.successCount === 1
            ? '1 item permanently deleted'
            : `${data.successCount} items permanently deleted`

        toast.success(successMessage)
      }

      // Show error toast for failed operations
      if (data.failureCount > 0) {
        const failureMessage =
          data.failureCount === 1
            ? '1 item failed to delete permanently'
            : `${data.failureCount} items failed to delete permanently`

        toast.error(failureMessage)
      }

      // Check if any deleted folders are currently being uploaded
      if (data.successfulIds && data.successfulIds.length > 0) {
        try {
          // Dynamically import to avoid circular dependencies
          import('@qse/ui').then(({ useGlobalFileUpload }) => {
            const uploadStore = useGlobalFileUpload.getState()

            // Filter to only track folder IDs that are currently being uploaded
            const currentlyUploadingFolderIds = uploadStore.getCurrentlyUploadingFolderIds()
            const affectedFolderIds = data.successfulIds.filter(deletedId =>
              currentlyUploadingFolderIds.includes(deletedId),
            )

            if (affectedFolderIds.length > 0) {
              console.log('🗑️ [EmptyTrash] Found affected folder uploads:', {
                affectedFolderIds,
                timestamp: new Date().toISOString(),
              })
              uploadStore.checkAndAbortAffectedFiles(affectedFolderIds)
            } else {
              console.log('🗑️ [EmptyTrash] No currently uploading folders were deleted')
            }
          })
        } catch (error) {
          console.error('Failed to check affected uploads:', error)
        }
      }

      // Clear trash data using setQueryData
      // Get all cached queries and clear those related to trash
      const allQueries = queryClient.getQueryCache().getAll()

      allQueries.forEach(query => {
        const queryKey = query.queryKey

        // Check if this is a files list query
        if (queryKey[0] === 'files' && queryKey[1] === 'list') {
          const searchParams = queryKey[2] as any

          // Check if this query is for trash location
          if (searchParams?.location === 'TRASH') {
            // Check if this is an infinite query by looking at the query state
            const queryState = queryClient.getQueryState(queryKey)

            if (
              queryState?.data &&
              typeof queryState.data === 'object' &&
              queryState.data !== null &&
              'pages' in queryState.data
            ) {
              // This is an infinite query - set empty pages structure
              queryClient.setQueryData(queryKey, {
                pages: [
                  {
                    pageInfo: {
                      hasPreviousPage: false,
                      hasNextPage: false,
                      totalCount: 0,
                      currentPage: 1,
                      totalPages: 0,
                    },
                    data: [],
                  },
                ],
                pageParams: [0],
              })
            } else {
              // This is a regular query - set empty data structure
              queryClient.setQueryData(queryKey, {
                pageInfo: {
                  hasPreviousPage: false,
                  hasNextPage: false,
                  totalCount: 0,
                  currentPage: 1,
                  totalPages: 0,
                },
                data: [],
              })
            }
          }
        }
      })
    },
    onError: error => {
      toast.error(`Failed to empty trash: ${error.message}`)
    },
  })
}

export const useBulkAbortUploads = () => {
  const queryClient = useQueryClient()

  return useMutation<BulkOperationResponseDto, Error, BulkOperationDto>({
    mutationFn: bulkAbortUploads,
    onSuccess: (data, variables) => {
      toast.success(`${variables.fileIds.length} uploads aborted successfully`)

      // Update cache to remove aborted uploads
      const filesListKey = queryKeys.files.lists()
      const queriesToUpdate = queryClient.getQueriesData({ queryKey: filesListKey })

      queriesToUpdate.forEach(([queryKey, cacheData]: [any, any]) => {
        if (!cacheData) return

        const successfulIdsSet = new Set(data.successfulIds.map(String))

        // Handle infinite queries (pagination)
        if (cacheData.pages && Array.isArray(cacheData.pages)) {
          const updatedPages = cacheData.pages.map((page: any) => {
            if (!page.data || !Array.isArray(page.data)) return page
            const updatedData = page.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

            return {
              ...page,
              data: updatedData,
              totalCount: Math.max(
                0,
                (page.totalCount || 0) - (page.data.length - updatedData.length),
              ),
            }
          })

          queryClient.setQueryData(queryKey, { ...cacheData, pages: updatedPages })
        }
        // Handle regular queries
        else if (cacheData.data && Array.isArray(cacheData.data)) {
          const updatedData = cacheData.data.filter((f: any) => !successfulIdsSet.has(String(f.id)))

          queryClient.setQueryData(queryKey, {
            ...cacheData,
            data: updatedData,
            totalCount: Math.max(
              0,
              cacheData.totalCount - (cacheData.data.length - updatedData.length),
            ),
          })
        }
      })

      // Force active queries to refetch after cache update
      // This ensures UI updates immediately and components see the changes
      queryClient.invalidateQueries({ queryKey: filesListKey })
    },
    onError: error => {
      toast.error(`Failed to abort uploads: ${error.message}`)
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}

export const useCompleteFolderUpload = () => {
  const queryClient = useQueryClient()

  return useMutation<FolderUploadCompleteResponseDto, Error, string>({
    mutationFn: completeFolderUpload,
    onSuccess: (data, folderId) => {
      // Check if we're in trash or favorites context - skip cache updates if so
      if (shouldSkipCacheUpdatesWithLog('both', 'folder upload completion')) {
        return
      }

      console.log('📂 [useCompleteFolderUpload] Folder upload completed:', {
        folderId,
        success: data.success,
        message: data.message,
      })

      // Show success toast
      toast.success('Folder upload completed successfully')

      // Invalidate folder details to get fresh data
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.folderDetails(folderId),
      })

      // Invalidate file lists to ensure UI is updated
      queryClient.invalidateQueries({
        queryKey: queryKeys.files.lists(),
      })
    },
    onError: error => {
      console.error('📂 [useCompleteFolderUpload] Failed to complete folder upload:', error)
      toast.error(`Failed to complete folder upload: ${error.message}`)
    },
    // Disable caching and deduplication for upload mutations
    gcTime: 0,
    retry: false,
  })
}
